import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { FileText, AlertCircle, Download, Loader2, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { getRegistryReportVersions, getInspectionPersonnelDocuments } from '../../../../../redux/Project/materialsData/action';
import { REPORT_TYPES } from '../../../../../redux/ReportDrafts/types';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { ApproveButton } from '../../../../../components/ui/ApproveButton';

/** Normalize tab labels like "Hydro Testing/Flushing of pipe" → "hydro-testing-flushing-of-pipe" */
const normalizeTabName = (tabName?: string): string =>
  (tabName || '')
    .toLowerCase()
    .replace(/[\/\\]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const getRegistryReportTypesForTab = (
  registryType?: EquipmentReportsViewModalProps['registryType'],
  activeTabName?: string,
): string[] | null => {
  const normalizedTab = normalizeTabName(activeTabName);
  if (!normalizedTab) return null;

  if (registryType === 'Equipment') {
    if (normalizedTab === 'welding') return [REPORT_TYPES.EQUIPMENT_WELDING_REPORT];
    if (normalizedTab === 'nde') return [REPORT_TYPES.EQUIPMENT_NDE_REPORT];
    if (normalizedTab === 'qc') return [REPORT_TYPES.EQUIPMENT_QC_REPORT];
    if (normalizedTab === 'coating') return [REPORT_TYPES.EQUIPMENT_COATING_REPORT];
    if (normalizedTab === 'hydro-testing-flushing-of-pipe') return [REPORT_TYPES.EQUIPMENT_HYDRO_REPORT];
    if (normalizedTab === 'torquing') return [REPORT_TYPES.EQUIPMENT_TORQUING_REPORT];
    return null;
  }

  if (registryType === 'Consumable') {
    if (normalizedTab === 'coating') return [REPORT_TYPES.CONSUMABLE_COATING_REPORT];
    if (normalizedTab === 'nde') return [REPORT_TYPES.CONSUMABLE_NDE_REPORT];
    if (normalizedTab === 'hydro-testing-flushing-of-pipe') return [REPORT_TYPES.CONSUMABLE_HYDRO_REPORT];
    if (normalizedTab === 'torquing') return [REPORT_TYPES.CONSUMABLE_TORQUING_REPORT];
    return null;
  }

  return null;
};

const getConsumableReportsViewTitle = (activeTabName?: string): string => {
  const normalizedTab = normalizeTabName(activeTabName);
  if (normalizedTab === 'coating') return 'Consumable Coating Registry Reports';
  if (normalizedTab === 'nde') return 'Consumable NDE Registry Reports';
  if (normalizedTab === 'hydro-testing-flushing-of-pipe') return 'Consumable Hydro Testing Registry Reports';
  if (normalizedTab === 'torquing') return 'Consumable Torquing Registry Reports';
  return 'Consumable Registry Reports';
};

const getEquipmentReportsViewTitle = (activeTabName?: string): string => {
  const normalizedTab = normalizeTabName(activeTabName);
  if (normalizedTab === 'welding') return 'Equipment Welding Registry Reports';
  if (normalizedTab === 'nde') return 'Equipment NDE Registry Reports';
  if (normalizedTab === 'qc') return 'Equipment QC Registry Reports';
  if (normalizedTab === 'coating') return 'Equipment Coating Registry Reports';
  if (normalizedTab === 'hydro-testing-flushing-of-pipe') return 'Equipment Hydro Testing Registry Reports';
  if (normalizedTab === 'torquing') return 'Equipment Torquing Registry Reports';
  return 'Equipment Registry Reports';
};

interface PipeSizeData {
  pipeId: number | string;
  pipeName: string;
  size: number | string;
  wallThickness: number | string;
  projectTypeId?: number | string;
  projectType?: string;
  projectSubType?: string;
  displayName?: string;
}

interface EquipmentReportsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateClick?: () => void;
  projectId: number | string;
  projectName: string;
  pipeSizesData?: PipeSizeData[];
  showOnlyQC?: boolean;
  registryType?:
    | 'Equipment'
    | 'Consumable'
    | 'QC Inspection Personnel'
    | 'Hydro Testing Personnel'
    | 'Torquing Personnel'
    | 'Welder Certification & Qualification'
    | 'Miscellaneous'
    | 'Miscellaneous & Certificate of Conformity'
    | 'Production Assembly';
  activeTabName?: string;
  refresh?: () => void;
}

export const EquipmentReportsViewModal: React.FC<EquipmentReportsViewModalProps> = ({
  isOpen,
  onClose,
  onGenerateClick,
  projectId,
  projectName,
  pipeSizesData: pipeSizesDataProp = [],
  showOnlyQC = false,
  registryType,
  activeTabName,
  refresh
}) => {
  const dispatch = useDispatch();

  // Get registry report versions state from Redux
  const {
    registryReportVersionsLoading,
    registryReportVersions,
    registryReportVersionsError,
    inspectionPersonnelDocumentsLoading,
    inspectionPersonnelDocuments,
    inspectionPersonnelDocumentsError
  } = useSelector((state: any) => state.materialsData || {});

  const isWelderRegistry = registryType === 'Welder Certification & Qualification';
  const isPersonnelRegistryView =
    registryType === 'QC Inspection Personnel' ||
    registryType === 'Hydro Testing Personnel' ||
    registryType === 'Torquing Personnel';
  const isQCInspectionPersonnelView = isPersonnelRegistryView || showOnlyQC;
  /** Same API bucket as QC (inspection-personnel documents); filter by report type key differs. */
  const usesInspectionPersonnelDocuments = isQCInspectionPersonnelView || isWelderRegistry;
  const isMiscellaneousConformityBundle = registryType === 'Miscellaneous & Certificate of Conformity';
  const isProductionAssemblyView = registryType === 'Production Assembly';

  const isLoading = isMiscellaneousConformityBundle
    ? registryReportVersionsLoading || inspectionPersonnelDocumentsLoading
    : usesInspectionPersonnelDocuments
      ? inspectionPersonnelDocumentsLoading
      : registryReportVersionsLoading;
  const reportVersions = usesInspectionPersonnelDocuments ? inspectionPersonnelDocuments : registryReportVersions;
  const error = isMiscellaneousConformityBundle
    ? registryReportVersionsError || inspectionPersonnelDocumentsError
    : usesInspectionPersonnelDocuments
      ? inspectionPersonnelDocumentsError
      : registryReportVersionsError;

  // Versions expand/collapse state per report type group
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // PDF Viewer state
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);

  // Fetch reports when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      if (isMiscellaneousConformityBundle) {
        dispatch(getRegistryReportVersions(projectId));
        dispatch(getInspectionPersonnelDocuments(projectId));
      } else if (usesInspectionPersonnelDocuments) {
        dispatch(getInspectionPersonnelDocuments(projectId));
      } else {
        // Use registry reports for Equipment, Consumable, and NDE_REPORT
        // Filtering will handle showing only relevant reports
        dispatch(getRegistryReportVersions(projectId));
      }
    } else if (!isOpen) {
      // Reset state when modal closes
      setExpandedGroups(new Set());
      setViewerFile(null);
    }
  }, [isOpen, projectId, dispatch, usesInspectionPersonnelDocuments, isMiscellaneousConformityBundle]);

  // Format report type for display (e.g., EQUIPMENT_WELDING_REPORT -> Equipment Welding Report)
  const formatReportType = (reportType: string): string => {
    return reportType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Process versions - group by report type with latest and older versions
  const processedVersions = useMemo(() => {
    const sortDocsDesc = (docs: any[]) =>
      [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });

    if (isMiscellaneousConformityBundle) {
      const result: Record<string, { latest: any | null; older: any[]; total: number }> = {};
      const miscKey = REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT;
      const conformityKey = REPORT_TYPES.CERTIFICATE_OF_CONFORMITY_REPORT;

      const miscDocs = registryReportVersions?.[miscKey];
      if (Array.isArray(miscDocs) && miscDocs.length > 0) {
        const sorted = sortDocsDesc(miscDocs);
        result[miscKey] = {
          latest: sorted[0] || null,
          older: sorted.slice(1),
          total: sorted.length
        };
      }

      let conformityDocs: any[] = [];
      const ipd = inspectionPersonnelDocuments;
      if (ipd && typeof ipd === 'object') {
        const preferred = ipd[conformityKey];
        if (Array.isArray(preferred) && preferred.length) {
          conformityDocs = preferred;
        } else {
          const altKey = Object.keys(ipd).find(
            (k) =>
              k !== 'QC_INSPECTION_PERSONNEL_REPORT' &&
              k !== 'HYDRO_PERSONNEL_REPORT' &&
              k !== 'TORQUING_PERSONNEL_REPORT' &&
              k !== 'WELDER_CERTIFICATION_QUALIFICATION_REPORT' &&
              k.toUpperCase().includes('CONFORMITY') &&
              Array.isArray(ipd[k]) &&
              ipd[k].length
          );
          if (altKey) conformityDocs = ipd[altKey] || [];
        }
      }
      if (conformityDocs.length > 0) {
        const sorted = sortDocsDesc(conformityDocs);
        result[conformityKey] = {
          latest: sorted[0] || null,
          older: sorted.slice(1),
          total: sorted.length
        };
      }

      return result;
    }

    if (!reportVersions || typeof reportVersions !== 'object') {
      return {};
    }
    
    const result: Record<string, { latest: any | null; older: any[]; total: number }> = {};
    const tabReportTypes = getRegistryReportTypesForTab(registryType, activeTabName);
    
    Object.keys(reportVersions).forEach((reportType) => {
      // Filter by registry type
      if (isWelderRegistry) {
        if (reportType !== 'WELDER_CERTIFICATION_QUALIFICATION_REPORT') {
          return;
        }
      } else if (
        registryType === 'QC Inspection Personnel' ||
        registryType === 'Hydro Testing Personnel' ||
        registryType === 'Torquing Personnel'
      ) {
        const expected =
          registryType === 'Hydro Testing Personnel'
            ? REPORT_TYPES.HYDRO_PERSONNEL_REPORT
            : registryType === 'Torquing Personnel'
            ? REPORT_TYPES.TORQUING_PERSONNEL_REPORT
            : REPORT_TYPES.QC_INSPECTION_PERSONNEL_REPORT;
        if (reportType !== expected) {
          return;
        }
      } else if (showOnlyQC) {
        // Project-level reports: QC / Hydro / Torquing personnel + welder qualification
        if (
          reportType !== 'QC_INSPECTION_PERSONNEL_REPORT' &&
          reportType !== REPORT_TYPES.HYDRO_PERSONNEL_REPORT &&
          reportType !== REPORT_TYPES.TORQUING_PERSONNEL_REPORT &&
          reportType !== 'WELDER_CERTIFICATION_QUALIFICATION_REPORT'
        ) {
          return;
        }
      } else if (registryType === 'Equipment') {
        if (tabReportTypes) {
          if (!tabReportTypes.includes(reportType)) return;
        } else if (!reportType.startsWith('EQUIPMENT_')) {
          return;
        }
      } else if (registryType === 'Consumable') {
        if (tabReportTypes) {
          if (!tabReportTypes.includes(reportType)) return;
        } else if (!reportType.startsWith('CONSUMABLE_')) {
          return;
        }
      } else if (registryType === 'Miscellaneous') {
        if (reportType !== REPORT_TYPES.REGISTRY_MISCELLANEOUS_REPORT) {
          return;
        }
      } else if (isProductionAssemblyView) {
        if (reportType !== REPORT_TYPES.PRODUCTION_ASSEMBLY_REPORT) {
          return;
        }
      }
      
      const docs = reportVersions[reportType] || [];
      if (docs.length === 0) return;
      
      // Sort by docRef descending (latest first)
      const sorted = [...docs].sort((a: any, b: any) => {
        const refA = a.docRef || 0;
        const refB = b.docRef || 0;
        return refB - refA;
      });
      
      result[reportType] = {
        latest: sorted[0] || null,
        older: sorted.slice(1),
        total: sorted.length
      };
    });
    
    return result;
  }, [
    reportVersions,
    showOnlyQC,
    registryType,
    isWelderRegistry,
    isProductionAssemblyView,
    isMiscellaneousConformityBundle,
    registryReportVersions,
    inspectionPersonnelDocuments,
    activeTabName,
  ]);

  // Get total document count across all report types
  const totalDocumentsCount = useMemo(() => {
    return Object.values(processedVersions).reduce((sum, group) => sum + (group?.total || 0), 0);
  }, [processedVersions]);

  // Toggle group expansion
  const toggleGroupExpansion = (reportType: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportType)) {
        newSet.delete(reportType);
      } else {
        newSet.add(reportType);
      }
      return newSet;
    });
  };

  // Handle download/view
  const handleDownloadReport = (doc: any) => {
    if (!doc || !(doc.fileUrl || doc.file_url)) {
      return;
    }
    const url = doc.fileUrl || doc.file_url;
    const fileName = doc.fileName || doc.file_name || 'Equipment Registry Report';
    setViewerFile({ fileUrl: url, fileName });
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Close PDF viewer
  const closeViewer = () => {
    setViewerFile(null);
  };


  // If viewer is open, render only the viewer
  if (viewerFile) {
    return (
      <PdfViewer
        fileUrl={viewerFile.fileUrl}
        fileName={viewerFile.fileName}
        onClose={closeViewer}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[1100px] w-[95vw] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-page-bg"
      >
          {/* Header */}
          <DialogHeader className="flex-shrink-0 pl-4 sm:pl-6 pr-14 py-4 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                {isWelderRegistry
                  ? 'Welder Certification & Qualification Reports'
                  : showOnlyQC
                  ? 'Personnel & Welder Qualification Reports'
                  : registryType === 'QC Inspection Personnel' ||
                    registryType === 'Hydro Testing Personnel' ||
                    registryType === 'Torquing Personnel'
                  ? `${registryType} Reports`
                  : registryType === 'Consumable'
                  ? getConsumableReportsViewTitle(activeTabName)
                  : registryType === 'Miscellaneous'
                  ? 'Miscellaneous Registry Reports'
                  : isProductionAssemblyView
                  ? 'Production Assembly Reports'
                  : isMiscellaneousConformityBundle
                  ? 'Miscellaneous & Certificate of Conformity Reports'
                  : getEquipmentReportsViewTitle(activeTabName)}
              </DialogTitle>
              <p className="text-[12px] text-heritage-grey mt-1 truncate">
                Project: {projectName}
              </p>
            </div>
            {onGenerateClick && (
              <Button
                type="button"
                onClick={onGenerateClick}
                className="flex-shrink-0 bg-hero-red hover:bg-hero-red-60 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            )}
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 relative">
            <div className="space-y-6">
              {/* Previous Reports / Version History - Grouped by Report Type */}
              <div className="space-y-3">
                {/* <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                  Previous Reports {!isLoading && `(${totalDocumentsCount})`}
                </h3> */}

                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-heritage-grey mr-2" />
                    <span className="text-[13px] text-heritage-grey">Loading previous reports...</span>
                  </div>
                ) : error ? (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                    <AlertCircle className="h-5 w-5 text-hero-red" />
                    <span className="text-[13px] text-hero-red">{error}</span>
                  </div>
                ) : Object.keys(processedVersions).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 bg-white border border-[#E2E5EA] rounded-xl">
                    <div className="w-12 h-12 rounded-xl bg-page-bg flex items-center justify-center mb-4">
                      <FileText className="h-6 w-6 text-heritage-grey" />
                    </div>
                    <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-1">
                      No Reports Yet
                    </h3>
                    <p className="text-[12px] text-heritage-grey max-w-xs text-center">
                      No reports have been generated for this registry. Click <span className="font-semibold text-text-grey">Generate Report</span> to create the first one.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(processedVersions).map(([reportType, { latest, older, total }]) => {
                      if (!latest) return null;

                      const isExpanded = expandedGroups.has(reportType);
                      const hasOlderVersions = older.length > 0;

                      return (
                        <div key={reportType} className="bg-white border border-[#E2E5EA] rounded-xl overflow-hidden">
                          {/* Category Header */}
                          <div className="px-4 py-2.5 bg-page-bg border-b border-[#E2E5EA] flex items-center justify-between">
                            <h4 className="text-[11px] font-semibold text-heritage-grey uppercase tracking-wide">
                              {formatReportType(reportType)}
                            </h4>
                            <span className="text-[11px] text-heritage-grey">
                              {total} {total === 1 ? 'version' : 'versions'}
                            </span>
                          </div>

                          {/* Latest Version row */}
                          <div className="flex items-center gap-3 px-4 py-3 hover:bg-page-bg transition-colors">
                            <div className="flex items-center gap-2 flex-shrink-0 min-w-[110px]">
                              {latest.docRef && (
                                <span className="text-[11px] px-2 py-0.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full font-semibold">
                                  v{latest.docRef}
                                </span>
                              )}
                              <span className="text-[11px] px-2 py-0.5 text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-full font-semibold">
                                Latest
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 flex items-baseline gap-2 text-[12px] text-text-grey">
                              <span>{formatDate(latest.uploadDate || '')}</span>
                              {latest.uploadedBy && (
                                <>
                                  <span className="text-heritage-grey">·</span>
                                  <span className="text-heritage-grey truncate">{latest.uploadedBy}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReport(latest)}
                                className="flex items-center gap-1.5 border-[#E2E5EA] text-text-grey hover:bg-page-bg h-8 px-3 text-[12px]"
                                disabled={!latest.fileUrl}
                              >
                                <Download className="h-3.5 w-3.5" />
                                Download
                              </Button>
                              <ApproveButton
                                docId={latest.docId}
                                isApproved={latest.approvalStatus === 'approved'}
                                apiType="registry"
                                reportType={reportType}
                                onStatusChange={() => refresh?.()}
                              />
                            </div>
                          </div>

                          {/* Older versions toggle + rows */}
                          {hasOlderVersions && (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleGroupExpansion(reportType)}
                                className="w-full px-4 py-2 border-t border-[#E2E5EA] flex items-center gap-1.5 text-[12px] text-heritage-grey hover:bg-page-bg transition-colors"
                              >
                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                <span>{isExpanded ? 'Hide' : 'Show'} {older.length} older {older.length === 1 ? 'version' : 'versions'}</span>
                              </button>
                              {isExpanded && (
                                <div className="border-t border-[#E2E5EA]">
                                  {older.map((doc: any, index: number) => (
                                    <div
                                      key={doc.docId || index}
                                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-page-bg transition-colors border-t border-[#EEF0F3] first:border-t-0"
                                    >
                                      <div className="flex items-center flex-shrink-0 min-w-[110px]">
                                        {doc.docRef && (
                                          <span className="text-[11px] px-2 py-0.5 text-heritage-grey bg-[#EEF0F3] rounded-full font-medium">
                                            v{doc.docRef}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0 text-[12px] text-heritage-grey">
                                        <span>{formatDate(doc.uploadDate || '')}</span>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleDownloadReport(doc)}
                                          className="flex items-center gap-1.5 border-[#E2E5EA] text-text-grey hover:bg-white h-8 px-3 text-[12px]"
                                          disabled={!doc.fileUrl}
                                        >
                                          <Download className="h-3.5 w-3.5" />
                                          Download
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};

